
import { defineComponent, computed, onMounted, ref, Ref, watch } from "vue";
import axios from "axios";
import { store } from "@/store";
import { ActionTypes } from "@/store/actions";
import CloseSVG from "@/components/CloseSVG.vue";
import { StepManager } from "@/onboarding";
import {
  pointerRef,
  modalBodyRef,
  modalContainerRef,
  reactiveWithRefText,
  reactiveWithRefImage,
  reactiveWithRefTextTool,
  reactiveWithRefSettings,
  reactiveWithRefZoomSlider,
  reactiveWithRefPrint,
  reactiveWithRefSaveDesign,
  reactiveWithRefSaveContinue,
} from "@/onboarding/Refs";

import { pointerWidth, pointerHeight } from "@/onboarding/Pointer";
//  ref<PublicComponentInstance<MyAppComponent>>

export default defineComponent({
  name: "App",
  components: {
    CloseSVG,
  },
  setup() {
    const labelType = computed(() => store.getters.currentLabelType);

    const stepManager = ref<StepManager>();

    //First panel doesn't use steps
    const totalSteps = computed(() => (stepManager.value ? stepManager.value.getLength() - 1 : 0));
    const currentStep = computed(() => stepManager.value?.getCurrentStep());
    const index = computed(() => currentStep.value?.index);
    const info = computed(() => currentStep.value?.info);
    const title = computed(() => currentStep.value?.title);

    onMounted(() => {
      if (pointerRef.value && modalBodyRef.value && modalContainerRef.value) {
        pointerRef.value
          .setStyle("width", pointerWidth)
          .setStyle("height", pointerHeight)
          .setStyle("transform", "rotate(45deg)");

        stepManager.value = new StepManager(
          pointerRef.value,
          modalBodyRef.value,
          modalContainerRef.value,
          [
            {
              title: "Welcome to SheetLabels.com",
              info: "Let's get started with a quick tour to show you around",
              modalBodySide: "none",
              pointer: {
                side: "none",
                centered: false,
              },
              reactive: undefined,
            },
            {
              title: "Add some text",
              info: "Click to add some text to the canvas",
              modalBodySide: "left",
              pointer: {
                side: "left",
                centered: false,
              },
              reactive: reactiveWithRefText,
            },
            {
              title: "Add an image",
              info: "Click to upload a photo, logo, or any image to your design",
              modalBodySide: "left",
              pointer: {
                side: "left",
                centered: false,
              },
              reactive: reactiveWithRefImage,
            },
            {
              title: "Customize an object",
              info: "To customize an object, click on it, and the properties panel will open",
              modalBodySide: "left",
              pointer: {
                side: "left",
                centered: true,
              },
              reactive: reactiveWithRefTextTool,
            },
            {
              title: "Utilize editor settings",
              info:
                "Turn On/Off Guidelines, Rulers, or Gridlines to help measure and align your canvas objects",
              modalBodySide: "left",
              pointer: {
                side: "left",
                centered: false,
              },
              reactive: reactiveWithRefSettings,
            },
            {
              title: "Zoom and pan the canvas",
              info:
                "Use the slider to zoom in and use the scroll bars, mouse wheel, or shift & left click to pan",
              modalBodySide: "bottom",
              pointer: {
                side: "bottom",
                centered: false,
              },
              reactive: reactiveWithRefZoomSlider,
            },
            {
              title: "Save your design",
              info:
                "Save your design to your Sheetlabels.com account to edit, print or order later",
              modalBodySide: "top",
              pointer: {
                side: "top",
                centered: false,
              },
              reactive: reactiveWithRefSaveDesign,
            },
          ]
        );
      }

      if (labelType.value.length) {
        addStepsBasedOnLabelType(stepManager, modalBodyRef, modalContainerRef, pointerRef);
      }
    });

    //This one runs once labelType has been set on app initialization
    //If we addStepsBasedOnLabelType in onMounted in this component, there is no current label type set yet
    watch(labelType, () => {
      addStepsBasedOnLabelType(stepManager, modalBodyRef, modalContainerRef, pointerRef);
    });

    const addStepsBasedOnLabelType = (
      stepManager: Ref<StepManager | undefined>,
      modalBodyRef: Ref<HTMLDivElement | undefined>,
      modalContainerRef: Ref<HTMLDivElement | undefined>,
      pointerRef: Ref<HTMLDivElement | undefined>
    ) => {
      if (stepManager.value && modalBodyRef.value && modalContainerRef.value && pointerRef.value) {
        if (labelType.value === "blank") {
          stepManager.value.addStep({
            title: "Print your labels",
            info: "Download a PDF template of your design to print, or have us print it for you!",
            modalBodySide: "top",
            pointer: {
              side: "top",
              centered: false,
            },
            reactive: reactiveWithRefPrint,
          });
        } else {
          stepManager.value.addStep({
            title: "Add to cart",
            info: "Review your design and add the label to your cart",
            modalBodySide: "top",
            pointer: {
              side: "top",
              centered: false,
            },
            reactive: reactiveWithRefSaveContinue,
          });
        }
      }
    };

    const incrementStep = () => {
      if (stepManager.value) {
        stepManager.value.incrementStep();
      }
    };

    const closeOnboarding = async () => {
      document.cookie = `OBC=; max-age=7884000; Secure`;

      try {
        axios.post(`${process.env.VUE_APP_API_URL}/editor/customers/ajax.php?mode=set_onboarding`, {
          onboarding: 1,
        });
      } catch (e) {
        console.log(e);
      }

      store.dispatch(ActionTypes.SET_SHOW_ONBOARDING, false);
    };

    return {
      index,
      totalSteps,
      info,
      title,
      pointerRef,
      modalBodyRef,
      modalContainerRef,
      incrementStep,
      closeOnboarding,
    };
  },
});
